import { useState } from 'react'
import { useAction, useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Star,
  Loader2,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  SkipForward,
  Users,
  ShieldCheck,
  XCircle,
  RefreshCw,
} from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'
import { cn } from '@/lib/utils'

const STAR_LABELS = ['', 'Not great', 'Needs work', 'Decent', 'Really good', 'Nailed it']

const CANCELLATION_FEEDBACK_LABELS: Record<string, string> = {
  customer_service: 'Customer service',
  low_quality: 'Low quality',
  missing_features: 'Missing features',
  other: 'Other',
  switched_service: 'Switched service',
  too_complex: 'Too complex',
  too_expensive: 'Too expensive',
  unused: 'Unused',
}

const CANCELLATION_REASON_LABELS: Record<string, string> = {
  cancellation_requested: 'User requested',
  payment_disputed: 'Payment disputed',
  payment_failed: 'Payment failed',
}

function formatTime(timestamp: number): string {
  const now = Date.now()
  const diffMs = now - timestamp
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHrs = Math.floor(diffMin / 60)
  if (diffHrs < 24) return `${diffHrs}h ago`
  const diffDays = Math.floor(diffHrs / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatAmount(amount?: number, currency?: string): string | null {
  if (amount == null) return null
  const value = amount / 100
  const cur = (currency || 'USD').toUpperCase()
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(value)
  } catch {
    return `${cur} ${value.toFixed(2)}`
  }
}

type Tab = 'user' | 'moderator' | 'cancellations'

const TABS: Tab[] = ['user', 'moderator', 'cancellations']

function parseTabFromSearch(params: URLSearchParams): Tab {
  const explicit = params.get('tab')
  if (explicit && TABS.includes(explicit as Tab)) return explicit as Tab
  for (const t of TABS) {
    if (params.has(t)) return t
  }
  return 'user'
}

export default function AdminFeedbackPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(0)
  const [showSkipped, setShowSkipped] = useState(false)
  const [onlyWithComment, setOnlyWithComment] = useState(true)
  const tab = parseTabFromSearch(searchParams)
  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams)
    for (const t of TABS) params.delete(t)
    if (next === 'user') params.delete('tab')
    else params.set('tab', next)
    setSearchParams(params, { replace: true })
  }
  const [syncing, setSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const limit = 30

  const isFeedbackTab = tab === 'user' || tab === 'moderator'

  const feedbackResult = useQuery(
    api.exportFeedback.listAll,
    isAdmin && isFeedbackTab
      ? { limit, offset: page * limit, kind: tab as 'user' | 'moderator' }
      : 'skip'
  )

  const cancellationsResult = useQuery(
    api.stripeCancellations.listAll,
    isAdmin && tab === 'cancellations'
      ? { limit, offset: page * limit, onlyWithComment }
      : 'skip'
  )

  const syncCancellations = useAction(api.stripeCancellations.syncFromStripe)

  if (!isAdmin) {
    return <div className="p-6 text-sm text-neutral-500">Admin access required</div>
  }

  const handleSync = async () => {
    setSyncing(true)
    setSyncMessage(null)
    try {
      const result = await syncCancellations({})
      setSyncMessage(
        `Synced: ${result.scanned} scanned · ${result.created} new · ${result.updated} updated · ${result.skipped} skipped`
      )
    } catch (e: any) {
      setSyncMessage(`Sync failed: ${e?.message || 'unknown error'}`)
    } finally {
      setSyncing(false)
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">
            {tab === 'cancellations' ? 'Cancellation Feedback' : 'Export Feedback'}
          </h1>
          <p className="text-sm text-neutral-500 mt-0.5">
            {tab === 'user' && 'User ratings and feedback on exported videos'}
            {tab === 'moderator' && 'Moderator quality reviews on exported videos'}
            {tab === 'cancellations' && 'Stripe subscription cancellation reasons and comments'}
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-neutral-200 bg-white p-0.5">
          <button
            onClick={() => { setTab('user'); setPage(0) }}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
              tab === 'user'
                ? 'bg-neutral-900 text-white'
                : 'text-neutral-500 hover:text-neutral-900'
            )}
          >
            <Users className="h-3.5 w-3.5" />
            User Feedback
          </button>
          <button
            onClick={() => { setTab('moderator'); setPage(0) }}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
              tab === 'moderator'
                ? 'bg-neutral-900 text-white'
                : 'text-neutral-500 hover:text-neutral-900'
            )}
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            Moderator Feedback
          </button>
          <button
            onClick={() => { setTab('cancellations'); setPage(0) }}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
              tab === 'cancellations'
                ? 'bg-neutral-900 text-white'
                : 'text-neutral-500 hover:text-neutral-900'
            )}
          >
            <XCircle className="h-3.5 w-3.5" />
            Cancellations
          </button>
        </div>
      </div>

      {tab === 'cancellations' ? (
        <CancellationsView
          result={cancellationsResult}
          page={page}
          setPage={setPage}
          limit={limit}
          onlyWithComment={onlyWithComment}
          setOnlyWithComment={setOnlyWithComment}
          syncing={syncing}
          syncMessage={syncMessage}
          onSync={handleSync}
        />
      ) : (
        <FeedbackView
          result={feedbackResult}
          page={page}
          setPage={setPage}
          limit={limit}
          showSkipped={showSkipped}
          setShowSkipped={setShowSkipped}
        />
      )}
    </div>
  )
}

interface FeedbackViewProps {
  result: any
  page: number
  setPage: (fn: (p: number) => number) => void
  limit: number
  showSkipped: boolean
  setShowSkipped: (v: boolean) => void
}

function FeedbackView({
  result,
  page,
  setPage,
  limit,
  showSkipped,
  setShowSkipped,
}: FeedbackViewProps) {
  if (result === undefined) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    )
  }

  const { items, total } = result
  const filtered = showSkipped ? items : items.filter((f: any) => !f.skipped)
  const totalPages = Math.ceil(total / limit)

  const submitted = items.filter((f: any) => !f.skipped)
  const skipped = items.filter((f: any) => f.skipped)
  const avgRating = submitted.length > 0
    ? Math.round((submitted.reduce((s: number, f: any) => s + f.rating, 0) / submitted.length) * 10) / 10
    : 0

  return (
    <>
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Total" value={total} />
        <StatCard label="Submitted" value={submitted.length} icon={<MessageSquare className="h-4 w-4 text-neutral-300" />} />
        <StatCard label="Skipped" value={skipped.length} icon={<SkipForward className="h-4 w-4 text-neutral-300" />} />
        <div className="rounded-lg border border-neutral-200 bg-white px-4 py-3">
          <div className="text-[11px] uppercase tracking-wide text-neutral-400">Avg Rating</div>
          <div className="text-xl font-semibold text-neutral-900 mt-0.5 flex items-center gap-1.5">
            {avgRating || '—'}
            {avgRating > 0 && <Star className="h-4 w-4 fill-amber-400 text-amber-400" />}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={() => setShowSkipped(!showSkipped)}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
            showSkipped
              ? 'bg-neutral-900 text-white border-neutral-900'
              : 'bg-white text-neutral-500 border-neutral-200 hover:border-neutral-300'
          }`}
        >
          {showSkipped ? 'Showing all' : 'Hiding skipped'}
        </button>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="text-center text-sm text-neutral-400 py-12">No feedback yet</div>
        ) : (
          filtered.map((fb: any) => (
            <div key={fb._id} className="rounded-lg border border-neutral-200 bg-white px-4 py-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {fb.skipped ? (
                    <div className="flex items-center gap-1.5 text-neutral-400">
                      <SkipForward size={14} />
                      <span className="text-xs">Skipped</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star
                            key={s}
                            size={14}
                            className={s <= fb.rating ? 'fill-amber-400 text-amber-400' : 'text-neutral-200'}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-neutral-500">{STAR_LABELS[fb.rating] || ''}</span>
                    </div>
                  )}

                  {fb.feedback && (
                    <p className="text-sm text-neutral-700 mt-1.5 leading-relaxed">{fb.feedback}</p>
                  )}

                  <div className="flex items-center gap-2 mt-2 text-[11px] text-neutral-400">
                    {fb.projectTitle && (
                      <Link
                        to={`/admin/videos/detail/${fb.projectShortcode || fb.workflowId}`}
                        className="text-blue-500 hover:underline"
                      >
                        {fb.projectTitle}
                      </Link>
                    )}
                    {fb.userEmail && (
                      <>
                        <span className="text-neutral-300">·</span>
                        <span>{fb.userName || fb.userEmail}</span>
                      </>
                    )}
                    <span className="text-neutral-300">·</span>
                    <span>{formatTime(fb.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} setPage={setPage} />
    </>
  )
}

interface CancellationsViewProps {
  result: any
  page: number
  setPage: (fn: (p: number) => number) => void
  limit: number
  onlyWithComment: boolean
  setOnlyWithComment: (v: boolean) => void
  syncing: boolean
  syncMessage: string | null
  onSync: () => void
}

function CancellationsView({
  result,
  page,
  setPage,
  limit,
  onlyWithComment,
  setOnlyWithComment,
  syncing,
  syncMessage,
  onSync,
}: CancellationsViewProps) {
  if (result === undefined) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    )
  }

  const { items, total, totalAll, withCommentCount, withReasonCount } = result
  const totalPages = Math.ceil(total / limit)

  const reasonCounts: Record<string, number> = {}
  for (const c of items) {
    if (c.feedback) {
      reasonCounts[c.feedback] = (reasonCounts[c.feedback] || 0) + 1
    }
  }
  const topReason = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1])[0]

  return (
    <>
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Total Cancellations" value={totalAll} />
        <StatCard
          label="With Comment"
          value={withCommentCount}
          icon={<MessageSquare className="h-4 w-4 text-neutral-300" />}
        />
        <StatCard
          label="With Reason"
          value={withReasonCount}
          icon={<XCircle className="h-4 w-4 text-neutral-300" />}
        />
        <div className="rounded-lg border border-neutral-200 bg-white px-4 py-3">
          <div className="text-[11px] uppercase tracking-wide text-neutral-400">Top Reason (page)</div>
          <div className="text-sm font-medium text-neutral-900 mt-0.5 truncate">
            {topReason
              ? `${CANCELLATION_FEEDBACK_LABELS[topReason[0]] || topReason[0]} (${topReason[1]})`
              : '—'}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setOnlyWithComment(!onlyWithComment)}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
            onlyWithComment
              ? 'bg-neutral-900 text-white border-neutral-900'
              : 'bg-white text-neutral-500 border-neutral-200 hover:border-neutral-300'
          }`}
        >
          {onlyWithComment ? 'Only with comments' : 'Showing all'}
        </button>
        <button
          onClick={onSync}
          disabled={syncing}
          className="text-xs px-2.5 py-1 rounded-full border bg-white text-neutral-700 border-neutral-200 hover:border-neutral-300 disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          <RefreshCw className={cn('h-3 w-3', syncing && 'animate-spin')} />
          {syncing ? 'Syncing...' : 'Sync from Stripe'}
        </button>
        {syncMessage && (
          <span className="text-xs text-neutral-500">{syncMessage}</span>
        )}
      </div>

      <div className="space-y-2">
        {items.length === 0 ? (
          <div className="text-center text-sm text-neutral-400 py-12">
            {totalAll === 0
              ? 'No cancellations synced yet — click "Sync from Stripe" above'
              : onlyWithComment
              ? 'No cancellations with text comments'
              : 'No cancellations match this filter'}
          </div>
        ) : (
          items.map((c: any) => {
            const feedbackLabel = c.feedback
              ? CANCELLATION_FEEDBACK_LABELS[c.feedback] || c.feedback
              : null
            const reasonLabel = c.reason
              ? CANCELLATION_REASON_LABELS[c.reason] || c.reason
              : null
            const amount = formatAmount(c.amount, c.currency)
            return (
              <div key={c._id} className="rounded-lg border border-neutral-200 bg-white px-4 py-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {feedbackLabel ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-100">
                          {feedbackLabel}
                        </span>
                      ) : (
                        <span className="text-xs text-neutral-400">No feedback</span>
                      )}
                      {reasonLabel && reasonLabel !== feedbackLabel && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
                          {reasonLabel}
                        </span>
                      )}
                    </div>

                    {c.comment && (
                      <p className="text-sm text-neutral-700 mt-1.5 leading-relaxed whitespace-pre-wrap">
                        {c.comment}
                      </p>
                    )}

                    <div className="flex items-center gap-2 mt-2 text-[11px] text-neutral-400 flex-wrap">
                      {(c.userName || c.userEmail) && (
                        <span>{c.userName || c.userEmail}</span>
                      )}
                      {c.userName && c.userEmail && (
                        <>
                          <span className="text-neutral-300">·</span>
                          <span>{c.userEmail}</span>
                        </>
                      )}
                      {amount && (
                        <>
                          <span className="text-neutral-300">·</span>
                          <span>{amount}</span>
                        </>
                      )}
                      <span className="text-neutral-300">·</span>
                      <span title={new Date(c.canceledAt).toLocaleString()}>
                        {formatTime(c.canceledAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} setPage={setPage} />
    </>
  )
}

function StatCard({
  label,
  value,
  icon,
}: {
  label: string
  value: number | string
  icon?: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-neutral-400">{label}</div>
      <div className="text-xl font-semibold text-neutral-900 mt-0.5 flex items-center gap-2">
        {value}
        {icon}
      </div>
    </div>
  )
}

function Pagination({
  page,
  totalPages,
  setPage,
}: {
  page: number
  totalPages: number
  setPage: (fn: (p: number) => number) => void
}) {
  if (totalPages <= 1) return null
  return (
    <div className="flex items-center justify-between">
      <div className="text-xs text-neutral-400">
        Page {page + 1} of {totalPages}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
          className="p-1.5 rounded hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          disabled={page >= totalPages - 1}
          className="p-1.5 rounded hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
